import { Menu, Transition } from "@headlessui/react";
import { Bars3Icon } from "@heroicons/react/24/solid";
import { Fragment } from "react";

import { PopoverPanel, Small, Tooltip, type TopBarAction } from "@fronterahealth/frontera-ui-components";

const classNames = (...classes: string[]) => classes.filter(Boolean).join(" ");

interface DesktopTopBarUser {
  imageSrc: string;
  title: string;
  logout: () => void;
  orgName?: string;
}

interface DesktopTopBarProps {
  user: DesktopTopBarUser | null;
  sidePanelContent: React.ReactNode;
  topBarActions?: TopBarAction[];
  notifications?: TopBarAction;
  rightActions?: TopBarAction[];
}

export const DesktopTopBar: React.FC<DesktopTopBarProps> = ({
  user,
  sidePanelContent,
  topBarActions,
  notifications,
  rightActions,
}) => {
  return (
    <div
      className={`sticky top-0 z-40 flex h-14 items-center justify-between gap-x-6 border-b border-border-medium bg-bg-surface hidden py-4 pl-4 pr-6 lg:flex`}
    >
      <div className="flex items-center gap-4">
        <PopoverPanel
          direction="bottom-right"
          width="w-[288px]"
          height="max-h-[calc(100vh-80px)]"
          content={sidePanelContent}
        >
          <Tooltip text="Main Menu" showInfoIcon={false}>
            <div data-testid="menu-button" className="cursor-pointer pt-2 text-text-primary">
              <span className="sr-only">Open sidebar</span>
              <Bars3Icon className="h-6 w-6 text-text-secondary" aria-hidden="true" />
            </div>
          </Tooltip>
        </PopoverPanel>
        <img
          src="https://637423311956-prod-static.s3.us-west-2.amazonaws.com/Branding/Basecamp.svg"
          alt="Basecamp"
          className="h-6"
        />
      </div>

      <div className="flex items-center justify-center gap-4">
        {rightActions && rightActions.some((a) => a.isVisible) && (
          <>
            {rightActions.map((action, index) => {
              if (!action.isVisible) return null;
              return (
                <Tooltip text={action.hint} showInfoIcon={false} key={index}>
                  <div
                    className={classNames(!action.isVisible ? "hidden" : "", "cursor-pointer")}
                    onClick={action.onAction}
                    data-testid={`${action.hint.toLowerCase().replaceAll(" ", "-")}-button`}
                  >
                    {action.content}
                  </div>
                </Tooltip>
              );
            })}
          </>
        )}

        {notifications && notifications.isVisible && (
          <Tooltip text={notifications.hint} showInfoIcon={false}>
            <div
              className="cursor-pointer"
              onClick={notifications.onAction}
              data-testid={`${notifications.hint.toLowerCase().replaceAll(" ", "-")}-button`}
            >
              {notifications.content}
            </div>
          </Tooltip>
        )}

        {topBarActions && topBarActions.some((a) => a.isVisible) && (
          <>
            {topBarActions.map((action, index) => (
              <Tooltip text={action.hint} showInfoIcon={false} key={index}>
                <div
                  className={classNames(!action.isVisible ? "hidden" : "", "cursor-pointer")}
                  onClick={action.onAction}
                  data-testid={`${action.hint.toLowerCase().replaceAll(" ", "-")}-button`}
                >
                  {action.content}
                </div>
              </Tooltip>
            ))}
            <div className="mr-2 h-6 border-r border-limestone-400" />
          </>
        )}

        {user ? (
          <Menu as="div" className="relative">
            <Menu.Button className="-m-1.5 flex items-center" data-testid="actions-button">
              <span className="sr-only">Open user menu</span>
              <Tooltip text="Account" showInfoIcon={false}>
                {user.imageSrc ? (
                  <img className="h-8 w-8 rounded-full object-cover" src={user.imageSrc} alt={user.title} />
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-globemallow-400">
                    <span className="text-xs font-semibold text-limestone-900">{user.title}</span>
                  </div>
                )}
              </Tooltip>
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-10 min-w-60 origin-top-right cursor-default rounded-md bg-bg-surface px-4 pb-2 pt-4 shadow-lg ring-1 ring-border-subtle focus:outline-none">
                <Menu.Item>
                  {() => (
                    <div className="flex flex-col gap-2 whitespace-nowrap border-b border-limestone-200 px-2 pb-2">
                      <Small colorType="primary">{user.title}</Small>
                      <Small>{user.orgName}</Small>
                    </div>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <span
                      onClick={() => user.logout()}
                      className={classNames(
                        active ? "bg-limestone-50" : "",
                        "mt-2 block cursor-pointer px-2 py-2 text-sm leading-6 text-text-primary",
                      )}
                      data-testid="logout-button"
                    >
                      Logout
                    </span>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>
        ) : null}
      </div>
    </div>
  );
};
